import React, { useContext } from 'react';

import { media } from 'context';
import { Button, Circle, Icon, Layout, Link, Modal, Text } from 'ds';
import { CLEANING_SERVICE_PATH, TENANT_ADMIN_PAYMENT_METHODS_PATH } from 'routes';
import { GRAY_700_HEX } from 'shared';

interface Props {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  useCalendarLink?: boolean;
}

const CleaningServiceCompareModal: React.FC<Props> = ({ isVisible, setIsVisible, useCalendarLink = true }) => {
  const { isMobile } = useContext(media);

  return (
    <Modal
      width={500}
      isVisible={isVisible}
      onClose={() => setIsVisible(false)}
      header="About our cleaning services"
      footer={
        <Layout {...(isMobile ? {} : { justify: 'center' })}>
          <Button text="Got it" onClick={() => setIsVisible(false)} fullWidth={isMobile} type="primary" size="sm" />
        </Layout>
      }
      hasBottomSheetPaddingX
      headerAlign="left"
      bottomSheetIsRounded
    >
      <Layout paddingBottom={64}>
        <Layout direction="column" gap={16}>
          <Layout flex gap={isMobile ? 8 : undefined}>
            <Layout width={236}></Layout>
            <Layout equalWidth marginRight={isMobile ? -3 : undefined}>
              <Text size="body-sm" semibold align="center" wrap={false}>
                Touch-up
              </Text>
            </Layout>
            <Layout equalWidth marginRight={isMobile ? -10 : undefined}>
              <Text size="body-sm" semibold align="center" wrap={false}>
                Standard
              </Text>
            </Layout>
          </Layout>
          <Layout align="center">
            <Layout width={236}>
              <Text size="body-md" semibold>
                Tidying
              </Text>
              <Text size="body-sm" color="gray-400">
                Includes trash removal and dishwashing
              </Text>
            </Layout>
            <CheckCircle />
            <CheckCircle />
          </Layout>
          <Layout align="center">
            <Layout width={236}>
              <Text size="body-md" semibold>
                Replenishment
              </Text>
              <Text size="body-sm" color="gray-400">
                Refilling snacks, drinks, and other supplies
              </Text>
            </Layout>
            <CheckCircle />
            <CheckCircle />
          </Layout>
          <Layout align="center">
            <Layout width={236}>
              <Text size="body-md" semibold>
                Floors & surfaces
              </Text>
              <Text size="body-sm" color="gray-400">
                Mopping floors, sanitizing desks and door handles
              </Text>
            </Layout>
            <MinusCircle />
            <CheckCircle />
          </Layout>
          <Layout align="center">
            <Layout width={236}>
              <Text size="body-md" semibold>
                Bathrooms
              </Text>
              <Text size="body-sm" color="gray-400">
                Comprehensive cleaning and sanitization
              </Text>
            </Layout>
            <MinusCircle />
            <CheckCircle />
          </Layout>
        </Layout>

        <Layout direction="column" marginTop={36}>
          <Text size="headline-xs">FAQ</Text>
          <Layout direction="column" gap={16} marginTop={16}>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                What time do cleaners typically take place?
              </Text>
              <Text size="body-sm" color="gray-700">
                Cleaners arrive in the evenings after your workday to reduce disruptions. Note that for cleanings
                scheduled on Fridays, service may take place during the weekend (often on Saturday, but occasionally on
                Sunday). If you prefer your cleaners clean at a specific time, just let us know and we’ll do our best to
                accommodate your request.
              </Text>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                How often will I be charged for our cleaning service?
              </Text>
              <Text size="body-sm" color="gray-700">
                In order to ensure our vendors are paid in a timely manner, we charge your services card on file for all
                unpaid completed cleanings on the{' '}
                <span style={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: GRAY_700_HEX }}>
                  1st and 16th of every month.
                </span>{' '}
                You can{' '}
                <Link href={TENANT_ADMIN_PAYMENT_METHODS_PATH} size="body-sm" color="blue-500" semibold={false}>
                  change your payment method
                </Link>{' '}
                anytime.
              </Text>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                Will the cleaners bring their own equipment and cleaning supplies?
              </Text>
              <Text size="body-sm" color="gray-700">
                Yes! All supplies and eqipement are included in the price of the cleaning. Specific supplies can be
                requested, but cannot be guaranteed.
              </Text>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                What's the difference between a standard and a touch-up cleaning?
              </Text>
              <Layout direction="column" gap={24}>
                <Text size="body-sm" color="gray-700">
                  Our touch-up service consists of a general tidying of the space, plus trash removal, replenishment of
                  supplies, including snacks and beverages when applicable, and dishes.
                </Text>
                <Text size="body-sm" color="gray-700">
                  Our standard cleaning is a more comprehensive service. Along with everything included in our touch-up
                  service, it also includes sweeping and mopping of floors, vacuuming floors and soft seating, wiping
                  down most surfaces and sanitizing door handles, as well as a complete cleaning of each bathroom when
                  applicable.
                </Text>
              </Layout>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                Can I request more than one recurring cleaning per week?
              </Text>
              <Layout direction="column" gap={24}>
                <Text size="body-sm" color="gray-700">
                  Yes! In fact, for most companies, we recommend two touch-ups per week in addition to your included
                  weekly cleaning. Please note that any changes you make to your weekly cleaning schedule mid-month will
                  be charged for the entire month, and any adjustments made will be subject to additional charges.
                </Text>
              </Layout>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                Can I add a one-off cleaning on a specific day?
              </Text>
              <Layout direction="column" gap={24}>
                <Text size="body-sm" color="gray-700">
                  Absolutely! Visit{' '}
                  {useCalendarLink ? (
                    <Link href={`${CLEANING_SERVICE_PATH}/calendar`} size="body-sm" color="blue-500" semibold={false}>
                      your calendar
                    </Link>
                  ) : (
                    'your calendar'
                  )}{' '}
                  anytime to make changes to your cleaning schedule. Please note that we need a minimum of 2 business
                  days’ notice to dispatch a cleaning crew, and any additional services are subject to fees.
                </Text>
              </Layout>
            </Layout>
            <Layout direction="column" gap={4}>
              <Text size="body-md" semibold>
                What do I do if a cleaning is missed or it's not up to my standards?
              </Text>
              <Layout direction="column" gap={24}>
                <Text size="body-sm" color="gray-700">
                  Your feedback is incredibly valuable to ensure we’re continuing to improve our services. Please let us
                  know whenever you have positive or not-so-positive feedback.
                </Text>
              </Layout>
            </Layout>
          </Layout>
        </Layout>
      </Layout>
    </Modal>
  );
};

const CheckCircle = () => {
  return (
    <Layout equalWidth height={24} align="center" justify="center">
      <Circle dimension={32} color="blue-500">
        <Icon name="checkmark" size={16} color="white" stroke={2} />
      </Circle>
    </Layout>
  );
};

const MinusCircle = () => {
  return (
    <Layout equalWidth height={24} align="center" justify="center">
      <Circle dimension={32} color="gray-50">
        <Icon size={16} name="minus" color="gray-400" stroke={2} />
      </Circle>
    </Layout>
  );
};

export default CleaningServiceCompareModal;
